import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/useApp';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';
import { motion } from 'framer-motion';
import { GraduationCap, Shield, Search, MapPin, Star, Heart, Loader2, AlertCircle, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';

const FindLawyer = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useFirebaseAuth();
  const { isAuthenticated } = useApp();
  
  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedRating, setSelectedRating] = useState('all');
  const [favorites, setFavorites] = useState([]);
  const [lawyers, setLawyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userBookings, setUserBookings] = useState([]);

  // Load lawyers from Firebase
  const loadLawyersFromFirebase = async () => {
    try {
      console.log('🔍 Starting to load lawyers from Firebase...');
      console.log('🔐 Current auth state:', currentUser ? 'Authenticated' : 'Not authenticated');
      setLoading(true);
      setError(null);

      const lawyersRef = collection(db, 'lawyers');
      console.log('📚 Lawyers collection reference created');
      
      // First, let's try to get all lawyers without any filters to see if we can access the collection
      console.log('🔍 Testing basic collection access...');
      const basicQuerySnapshot = await getDocs(lawyersRef);
      console.log('📊 Basic query executed, got snapshot with', basicQuerySnapshot.size, 'total documents');
      
      basicQuerySnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('📄 Document ID:', doc.id, 'isVerified:', data.isVerified, 'name:', data.full_name);
      });
      
      // Now try the filtered query - first without orderBy to see if that's causing issues
      console.log('🔍 Trying query with just isVerified filter...');
      const q = query(lawyersRef, where('isVerified', '==', true));
      console.log('🔍 Query created with isVerified=true (no orderBy)');
      
      // Also try with string comparison in case isVerified is stored as string
      console.log('🔍 Also trying with string comparison...');
      const qString = query(lawyersRef, where('isVerified', '==', 'true'));
      console.log('🔍 Query created with isVerified="true" (string)');
      
      const querySnapshot = await getDocs(q);
      console.log('📊 Boolean query executed, got snapshot with', querySnapshot.size, 'documents');

      let lawyersData = [];
      querySnapshot.forEach((doc) => {
        const lawyerData = {
          id: doc.id,
          ...doc.data()
        };
        console.log('👨‍💼 Found verified lawyer (boolean):', lawyerData.full_name, 'ID:', doc.id, 'Rating:', lawyerData.average_rating);
        console.log('📋 Full lawyer data:', lawyerData);
        lawyersData.push(lawyerData);
      });
      
      // If no results with boolean query, try string query
      if (lawyersData.length === 0) {
        console.log('🔍 No results with boolean query, trying string query...');
        const querySnapshotString = await getDocs(qString);
        console.log('📊 String query executed, got snapshot with', querySnapshotString.size, 'documents');
        
        querySnapshotString.forEach((doc) => {
          const lawyerData = {
            id: doc.id,
            ...doc.data()
          };
          console.log('👨‍💼 Found verified lawyer (string):', lawyerData.full_name, 'ID:', doc.id, 'Rating:', lawyerData.average_rating);
          console.log('📋 Full lawyer data (string):', lawyerData);
          lawyersData.push(lawyerData);
        });
      }
      
      // Sort by average_rating (handle missing values)
      lawyersData.sort((a, b) => {
        const ratingA = a.average_rating || 0;
        const ratingB = b.average_rating || 0;
        return ratingB - ratingA; // Descending order
      });
      
      console.log('✅ Total verified lawyers loaded:', lawyersData.length);
      setLawyers(lawyersData);
      setLoading(false);
    } catch (error) {
      console.error('❌ Error loading lawyers from Firebase:', error);
      console.error('❌ Error details:', error.code, error.message);
      
      // If the filtered query fails, try to get all lawyers and filter client-side
      if (error.code === 'permission-denied' || error.code === 'unavailable') {
        console.log('🔄 Trying fallback approach - getting all lawyers and filtering client-side...');
        try {
          const lawyersRef = collection(db, 'lawyers');
          const allLawyersSnapshot = await getDocs(lawyersRef);
          
          const allLawyersData = [];
          allLawyersSnapshot.forEach((doc) => {
            const lawyerData = {
              id: doc.id,
              ...doc.data()
            };
            allLawyersData.push(lawyerData);
          });
          
          // Filter client-side for verified lawyers
          const verifiedLawyers = allLawyersData.filter(lawyer => lawyer.isVerified === true);
          console.log('✅ Fallback: Found', verifiedLawyers.length, 'verified lawyers out of', allLawyersData.length, 'total');
          
          // Sort by average_rating
          verifiedLawyers.sort((a, b) => {
            const ratingA = a.average_rating || 0;
            const ratingB = b.average_rating || 0;
            return ratingB - ratingA;
          });
          
          setLawyers(verifiedLawyers);
          setLoading(false);
          return;
        } catch (fallbackError) {
          console.error('❌ Fallback also failed:', fallbackError);
        }
      }
      
      setError('Failed to load lawyers from database');
      setLawyers([]);
      setLoading(false);
    }
  };

  // Load user bookings to filter out booked lawyers
  const loadUserBookings = async () => {
    if (!currentUser) return;
    
    try {
      console.log('🔍 Loading user bookings for:', currentUser.uid);
      
      // Load bookings from localStorage FIRST (for immediate display)
      const currentUserData = JSON.parse(localStorage.getItem('currentUser') || '{}');
      const userId = currentUserData.uid || currentUserData.id || 'anonymous';
      const userBookingsKey = `userBookings_${userId}`;
      const generalBookingsKey = `bookings_${userId}`;
      
      // Load from multiple localStorage sources
      const localStorageBookings = JSON.parse(localStorage.getItem(userBookingsKey) || '[]');
      const generalBookings = JSON.parse(localStorage.getItem(generalBookingsKey) || '[]');
      const oldUserBookings = JSON.parse(localStorage.getItem('userBookings') || '[]');
      const oldGeneralBookings = JSON.parse(localStorage.getItem('bookings') || '[]');
      
      // Combine all localStorage sources
      let allLocalBookings = [...localStorageBookings, ...generalBookings, ...oldUserBookings, ...oldGeneralBookings];
      
      // Remove duplicates
      allLocalBookings = allLocalBookings.filter((booking, index, self) => 
        index === self.findIndex(b => b.id === booking.id)
      );
      
      console.log('📋 Found localStorage bookings:', allLocalBookings.length);
      
      // Set bookings immediately from localStorage
      if (allLocalBookings.length > 0) {
        setUserBookings(allLocalBookings);
        console.log('📋 Set bookings from localStorage:', allLocalBookings.length);
      }
      
      // Then try to load from Firebase for sync (in background)
      setTimeout(async () => {
        try {
          const bookingsRef = collection(db, 'appointments');
          const q = query(bookingsRef, where('client_id', '==', currentUser.uid));
          const querySnapshot = await getDocs(q);
          
          const firebaseBookings = [];
          querySnapshot.forEach((doc) => {
            const bookingData = { id: doc.id, ...doc.data() };
            console.log('📋 Found Firebase booking:', bookingData);
            firebaseBookings.push(bookingData);
          });
          
          console.log('📋 Found Firebase bookings:', firebaseBookings.length);
          
          // Merge Firebase and localStorage data
          const allBookings = [...firebaseBookings];
          
          // Add localStorage bookings that aren't in Firebase
          allLocalBookings.forEach(localBooking => {
            const exists = allBookings.find(b => b.id === localBooking.id);
            if (!exists) {
              allBookings.push(localBooking);
            }
          });
          
          setUserBookings(allBookings);
          console.log('📋 Total user bookings loaded (merged):', allBookings.length);
          
          // Save merged data back to localStorage
          localStorage.setItem(userBookingsKey, JSON.stringify(allBookings));
          localStorage.setItem(generalBookingsKey, JSON.stringify(allBookings));
          
        } catch (firebaseError) {
          console.error('⚠️ Error loading from Firebase:', firebaseError);
          console.log('📱 Continuing with localStorage data only');
          // Keep using localStorage data if Firebase fails
        }
      }, 100); // Small delay to not block UI
      
    } catch (error) {
      console.error('❌ Error loading user bookings:', error);
      // Try to load from localStorage as fallback
      try {
        const currentUserData = JSON.parse(localStorage.getItem('currentUser') || '{}');
        const userId = currentUserData.uid || currentUserData.id || 'anonymous';
        const userBookingsKey = `userBookings_${userId}`;
        const fallbackBookings = JSON.parse(localStorage.getItem(userBookingsKey) || '[]');
        setUserBookings(fallbackBookings);
        console.log('📋 Using fallback localStorage data:', fallbackBookings.length);
      } catch (fallbackError) {
        console.error('❌ Fallback also failed:', fallbackError);
        setUserBookings([]);
      }
    }
  };



  // Load lawyers on component mount
  useEffect(() => {
    console.log('🚀 FindLawyer component mounted, starting to load lawyers...');
    console.log('🔐 Current user:', currentUser);
    loadLawyersFromFirebase();
    loadUserBookings();
  }, [currentUser]);

  // Listen for booking updates to refresh the list
  useEffect(() => {
    const handleBookingUpdate = () => {
      console.log('🔄 Booking updated, refreshing user bookings...');
      loadUserBookings();
    };

    window.addEventListener('bookingUpdated', handleBookingUpdate);
    return () => window.removeEventListener('bookingUpdated', handleBookingUpdate);
  }, []);

  // Filter lawyers based on search and filters (allow multiple bookings)
  const filteredLawyers = lawyers.filter(lawyer => {
    // Allow multiple bookings - don't filter out booked lawyers
    console.log('✅ Multiple bookings allowed - lawyer remains visible:', lawyer.full_name);
    
    const matchesSearch = searchTerm === '' || 
      lawyer.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lawyer.specialization?.some(spec => spec.toLowerCase().includes(searchTerm.toLowerCase())) ||
      lawyer.bar_council_state?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lawyer.location?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || 
      lawyer.specialization?.includes(selectedCategory);
    
    const matchesRating = selectedRating === 'all' || 
      (lawyer.average_rating && lawyer.average_rating >= parseFloat(selectedRating));
    
    return matchesSearch && matchesCategory && matchesRating;
  });

  // Debug logging
  console.log('🔍 Current lawyers state:', lawyers.length, 'lawyers');
  console.log('🔍 User bookings:', userBookings.length, 'bookings');
  console.log('🔍 User bookings data:', userBookings);
  console.log('🔍 Filtered lawyers:', filteredLawyers.length, 'lawyers');
  console.log('🔍 Search term:', searchTerm);
  console.log('🔍 Selected category:', selectedCategory);
  console.log('🔍 Selected rating:', selectedRating);
  
  // Log the first lawyer's available fields for debugging
  if (lawyers.length > 0) {
    console.log('🔍 First lawyer available fields:', Object.keys(lawyers[0]));
    console.log('🔍 First lawyer bar_council_state field:', lawyers[0].bar_council_state);
    console.log('🔍 First lawyer fees.consultation field:', lawyers[0].fees?.consultation);
  }

  // Handle favorites
  const toggleFavorite = (lawyerId) => {
    setFavorites(prev => 
      prev.includes(lawyerId) 
        ? prev.filter(id => id !== lawyerId)
        : [...prev, lawyerId]
    );
  };

  // Handle viewing lawyer profile
  const handleViewLawyer = (lawyer) => {
    if (!isAuthenticated) {
      toast.error('Please register and sign in to view lawyer details');
      navigate('/auth');
      return;
    }
    
    navigate(`/lawyer-detail/${lawyer.id}`, {
      state: { lawyer: lawyer }
    });
  };



  // Check if lawyer is booked by current user only
  const isLawyerBooked = (lawyerId) => {
    return userBookings.some(booking => {
      const bookingLawyerId = booking.lawyer_id || booking.lawyerId;
      const status = booking.status || 'pending';
      return bookingLawyerId === lawyerId && 
        ['pending', 'confirmed', 'ongoing', 'upcoming'].includes(status);
    });
  };

  // Get booking details for a specific lawyer
  const getLawyerBookingDetails = (lawyerId) => {
    const booking = userBookings.find(b => b.lawyer_id === lawyerId || b.lawyerId === lawyerId);
    if (booking) {
      return {
        id: booking.id,
        status: booking.status,
        appointmentDate: booking.appointmentDate,
        appointmentTime: booking.appointmentTime,
        lawyerName: booking.lawyerName
      };
    }
    return null;
  };

  // Render lawyer card
  const renderLawyerCard = (lawyer) => {
    const isFavorite = favorites.includes(lawyer.id);
    const isBooked = isLawyerBooked(lawyer.id);
    const bookingDetails = isBooked ? getLawyerBookingDetails(lawyer.id) : null;
    
    return (
      <motion.div
        key={lawyer.id}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className={`bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden ${
          isBooked ? 'border-2 border-red-200 bg-red-50' : ''
        }`}
      >
        <div className="p-6">
          {/* Booking Status Banner */}
          {isBooked && (
            <div className="mb-4 p-3 bg-red-100 border border-red-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <span className="text-sm font-medium text-red-800">
                    Already Booked - Consultation in Progress
                  </span>
                </div>
                <span className="px-2 py-1 text-xs bg-red-200 text-red-800 rounded-full font-medium capitalize">
                  {bookingDetails?.status || 'Active'}
                </span>
              </div>
              {bookingDetails && (
                <div className="mt-2 text-xs text-red-700">
                  <p>Booking ID: #{bookingDetails.id?.slice(-6) || 'N/A'}</p>
                  {bookingDetails.appointmentDate && (
                    <p>Date: {new Date(bookingDetails.appointmentDate).toLocaleDateString()}</p>
                  )}
                  {bookingDetails.appointmentTime && (
                    <p>Time: {bookingDetails.appointmentTime}</p>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-4">
              <img
                src={lawyer.profile_pic || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=300&fit=crop&crop=face'}
                alt={lawyer.full_name}
                className="w-16 h-16 rounded-full object-cover"
                onError={(e) => {
                  e.target.src = 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=300&fit=crop&crop=face';
                }}
              />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{lawyer.full_name}</h3>
                <div className="flex items-center space-x-2 mt-1">
                  <Shield className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-green-600 font-medium">Verified</span>
                </div>
              </div>
            </div>
            <button
              onClick={() => toggleFavorite(lawyer.id)}
              className="text-gray-400 hover:text-red-500 transition-colors"
            >
              {isFavorite ? <Heart className="w-5 h-5 fill-red-500 text-red-500" /> : <Heart className="w-5 h-5" />}
            </button>
          </div>

          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <GraduationCap className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-600">
                {lawyer.specialization?.join(', ') || 'General Law'}
              </span>
            </div>

            <div className="flex items-center space-x-2">
              <MapPin className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-600">
                {lawyer.bar_council_state || lawyer.location || lawyer.city || lawyer.address || 'Location not specified'}
              </span>
            </div>

            <div className="flex items-center space-x-2">
              <Star className="w-4 h-4 text-yellow-400 fill-current" />
              <span className="text-sm text-gray-600">
                {lawyer.average_rating || 0} ({lawyer.experience_years || 0} years experience)
              </span>
            </div>

            <p className="text-sm text-gray-600 line-clamp-2">{lawyer.bio || 'No bio available'}</p>

            <div className="flex items-center justify-between pt-3">
              <div className="text-lg font-semibold text-purple-600">
                ₹{lawyer.fees?.consultation || lawyer.consultation_fee || lawyer.fee || lawyer.price || 0}
              </div>
              <div className="flex space-x-2">
                {isBooked ? (
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleViewLawyer(lawyer)}
                      className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                      View Profile
                    </button>
                    <button
                      onClick={() => navigate('/my-bookings')}
                      className="px-3 py-1 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                    >
                      View Booking
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => handleViewLawyer(lawyer)}
                    className="px-3 py-1 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                  >
                    View Profile
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Find a Lawyer</h1>
          <p className="text-gray-600">Connect with qualified legal professionals.</p>
        </div>

        {/* Booking Restriction Info */}
        {userBookings.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 mt-1">
                <AlertTriangle className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-medium text-blue-800 mb-1">
                  Booking Restriction Notice
                </h3>
                <p className="text-sm text-blue-700 mb-2">
                  You have active bookings with some lawyers. You cannot book the same lawyer again until your current consultation ends.
                </p>
                <div className="text-xs text-blue-600">
                  <p>• Active bookings include: Pending, Confirmed, Upcoming, and Ongoing consultations</p>
                  <p>• You can still view profiles and manage existing bookings</p>
                  <p>• Once a consultation is completed or cancelled, you can book again</p>
                </div>
                <div className="mt-3">
                  <button
                    onClick={() => navigate('/my-bookings')}
                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    View My Bookings
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by name, specialization, or city..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">All Categories</option>
              <option value="Criminal Law">Criminal Law</option>
              <option value="Family Law">Family Law</option>
              <option value="Corporate Law">Corporate Law</option>
              <option value="Property Law">Property Law</option>
              <option value="Tax Law">Tax Law</option>
            </select>
            
            <select
              value={selectedRating}
              onChange={(e) => setSelectedRating(e.target.value)}
              className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">All Ratings</option>
              <option value="4.5">4.5+ stars</option>
              <option value="4.0">4.0+ stars</option>
              <option value="3.5">3.5+ stars</option>
            </select>
          </div>
        </div>

        {/* Results */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">
              {loading ? (
                <div className="flex items-center space-x-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Loading lawyers...</span>
                </div>
              ) : (
                `${filteredLawyers.length} lawyers found (${userBookings.length} bookings loaded)`
              )}
            </h2>
            <div className="flex items-center gap-2">
              {error && (
                <div className="flex items-center space-x-2 text-red-600">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm">{error}</span>
                </div>
              )}
            </div>
          </div>

          {/* Lawyers Grid */}
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-purple-600" />
              <p className="text-gray-600">Loading lawyers from database...</p>
            </div>
          ) : filteredLawyers.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredLawyers.map(renderLawyerCard)}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No lawyers found</h3>
              <p className="text-gray-600">Try adjusting your search criteria</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FindLawyer;